import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
	Accordion,
	ActionIcon,
	Button,
	Checkbox,
	HoverCard,
	Loader,
	Menu,
	Select,
	SegmentedControl,
} from "@mantine/core";
import {
	IconAlignJustified,
	IconBucket,
	IconCheck,
	IconChevronDown,
	IconChevronsDown,
	IconChevronsUp,
	IconCopy,
	IconDatabase,
	IconRefresh,
	IconSettings,
} from "@tabler/icons-react";
import ButtonWithPopoverFeedback from "../ButtonWithPopoverFeedback";
import { humanFileSize } from "../../utilities/utilities";
import { fetchDeveloperObjectInfo } from "../../actions/adminActions";
import {
	setSubheaderText,
	setSubheaderActions,
} from "../../actions/navigateActions";
import DevTransposedTable from "./DevTransposedTable";
import DevDataTable from "./DevDataTable";
import { CELL_BORDER } from "./DevTableUtils";
import { ColumnPane, SectionControl } from "./ColumnPane";
import EditableIdSubheaderTitle from "./EditableIdSubheaderTitle";
import TenantSelect from "./TenantSelect";

const OBJECT_TYPE_ROUTE_SEGMENT = {
	document: "docs",
	email: "email",
	note: "notes",
};

const OBJECT_TYPE_OPTIONS = [
	{ value: "document", label: "Document" },
	{ value: "email", label: "Email" },
	{ value: "note", label: "Note" },
];

function omitFields(data, fields) {
	if (!data) return data;
	if (Array.isArray(data)) {
		return data.map((row) => {
			const out = { ...row };
			fields.forEach((f) => delete out[f]);
			return out;
		});
	}
	const out = { ...data };
	fields.forEach((f) => delete out[f]);
	return out;
}

const DB_ACCORDION_STYLES = {
	item: { borderBottom: "1px solid #ececec" },
	control: { minHeight: 24, padding: "1px 0" },
	panel: { padding: 0 },
	content: { padding: "2px 0 4px 0" },
};

const TABLE_LABEL_STYLE = {
	display: "flex",
	alignItems: "center",
	gap: 6,
	fontFamily: "monospace",
	fontSize: 13,
	fontWeight: 600,
};

function TableLabel({ name, count, icon }) {
	return (
		<div style={TABLE_LABEL_STYLE}>
			{icon ?? (
				<IconDatabase
					size={14}
					style={{ flexShrink: 0, color: "#888" }}
				/>
			)}
			{name}
			{count != null && count > 0 ? (
				<span
					style={{
						fontWeight: 400,
						color: "#888",
						fontFamily: "monospace",
					}}
				>
					({count})
				</span>
			) : null}
		</div>
	);
}

function countDbRecords(database, objectType) {
	const r = database?.records;
	if (!r) return 0;
	if (objectType === "document") {
		return (
			(r.Documents ? 1 : 0) +
			(r.Folders?.length ?? 0) +
			(r.DocumentVersions?.length ?? 0) +
			(r.DocumentVersionCounters ? 1 : 0) +
			(r.DocumentVersionLabels?.length ?? 0) +
			(r.DocumentMajorVersions?.length ?? 0)
		);
	}
	if (objectType === "email")
		return (r.Emails ? 1 : 0) + (r.Folders?.length ?? 0);
	return (r.Notes ? 1 : 0) + (r.Folders?.length ?? 0);
}

function DbSubSection({
	label,
	icon,
	hasData,
	count,
	emptyLabel,
	headerExtra,
	openSections,
	onSectionsChange,
	children,
}) {
	if (!hasData) {
		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					padding: "2px 0",
					borderBottom: "1px solid #ececec",
				}}
			>
				<TableLabel name={label} icon={icon} />
				<span
					style={{ color: "#bbb", fontSize: 12, fontStyle: "italic" }}
				>
					{emptyLabel ?? (count != null ? "no records" : "no data")}
				</span>
			</div>
		);
	}

	return (
		<Accordion
			multiple
			value={openSections}
			onChange={onSectionsChange}
			transitionDuration={0}
			vars={() => ({ root: { "--accordion-control-height": "24px" } })}
			styles={DB_ACCORDION_STYLES}
		>
			<Accordion.Item value={label}>
				<Accordion.Control>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							width: "100%",
						}}
					>
						<div style={{ flex: 1, minWidth: 0 }}>
							<TableLabel
								name={label}
								count={count}
								icon={icon}
							/>
						</div>
						{headerExtra ? (
							<span onClick={(e) => e.stopPropagation()}>
								{headerExtra}
							</span>
						) : null}
					</div>
				</Accordion.Control>
				<Accordion.Panel>{children}</Accordion.Panel>
			</Accordion.Item>
		</Accordion>
	);
}

const DB_TOP_TD_LABEL = {
	padding: "4px 16px 4px 8px",
	color: "#555",
	fontWeight: 600,
	fontSize: 12,
	fontFamily: "monospace",
	whiteSpace: "nowrap",
	verticalAlign: "middle",
	width: "1px",
};
const DB_TOP_TD_VALUE = {
	padding: "4px 4px 4px 8px",
	verticalAlign: "middle",
	fontSize: 12,
	fontFamily: "monospace",
	wordBreak: "break-all",
};

function DbTopInfo({ records, objectType }) {
	const [hoveredRow, setHoveredRow] = useState(null);

	const folders = records?.Folders;
	const matterID = folders?.[0]?.matterID ?? null;
	const folderPath =
		folders?.length > 0
			? "/" + folders.map((f) => f.name).join("/") + "/"
			: null;

	const displayName =
		objectType === "document"
			? (records?.Documents?.filename ?? null)
			: objectType === "email"
				? (records?.Emails?.subjectline ?? null)
				: (records?.Notes?.name ?? null);

	const nameLabel =
		objectType === "document"
			? "filename"
			: objectType === "email"
				? "subjectline"
				: "name";

	const rows = [
		{ label: nameLabel, value: displayName },
		{ label: "matterID", value: matterID },
		{ label: "folder", value: folderPath },
	];

	return (
		<table
			style={{
				borderCollapse: "collapse",
				width: "100%",
				marginBottom: 8,
			}}
		>
			<tbody>
				{rows.map(({ label, value }) => (
					<tr
						key={label}
						style={{ borderBottom: "1px solid #e8e8e8" }}
						onMouseEnter={() => setHoveredRow(label)}
						onMouseLeave={() => setHoveredRow(null)}
					>
						<td style={DB_TOP_TD_LABEL}>{label}</td>
						<td
							style={{ ...DB_TOP_TD_VALUE, position: "relative" }}
						>
							{value != null ? (
								String(value)
							) : (
								<span
									style={{
										color: "#bbb",
										fontStyle: "italic",
									}}
								>
									null
								</span>
							)}
							{hoveredRow === label && value != null ? (
								<div
									style={{
										position: "absolute",
										right: 2,
										top: "50%",
										transform: "translateY(-50%)",
										background: "white",
										display: "flex",
										gap: 2,
									}}
								>
									<ButtonWithPopoverFeedback
										iconComponent={<IconCopy size={13} />}
										onClick={() =>
											navigator.clipboard.writeText(
												String(value)
											)
										}
										feedbackMessage="Copied"
										title={`Copy ${label}`}
									/>
								</div>
							) : null}
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

function DatabaseSection({
	database,
	objectType,
	openDbSections,
	onDbSectionsChange,
	showAllFields,
	friendlyValues,
	userMap,
	truncate,
}) {
	if (!database) {
		return <span style={{ color: "#aaa", fontSize: 12 }}>No data</span>;
	}

	const { records } = database;

	const props = {
		openSections: openDbSections,
		onSectionsChange: onDbSectionsChange,
	};

	const objIdField =
		objectType === "document"
			? "documentID"
			: objectType === "email"
				? "emailID"
				: "noteID";
	const idFields = showAllFields ? [] : ["id", "tenantID"];
	const refFields = showAllFields ? [] : [objIdField, "tenantID"];
	const folderFields = showAllFields ? [] : ["tenantID"];

	return (
		<div>
			{records ? (
				<div>
					<DbTopInfo records={records} objectType={objectType} />
					{objectType === "document" ? (
						<>
							<DbSubSection
								label="Documents"
								hasData={!!records.Documents}
								count={records.Documents ? 1 : 0}
								{...props}
							>
								<DevTransposedTable
									tableUniqueID="dev-obj-documents"
									data={omitFields(
										records.Documents,
										idFields
									)}
									cellRenderers={mergeCellRenderers(
										makeDateCellRenderers(
											DB_DOCUMENT_DATE_KEYS,
											friendlyValues
										),
										makeUserCellRenderers(
											DB_DOCUMENT_USER_KEYS,
											userMap,
											friendlyValues
										),
										makeSizeCellRenderers(
											DB_DOCUMENT_SIZE_KEYS,
											friendlyValues
										)
									)}
									truncate={truncate}
								/>
							</DbSubSection>
							<DbSubSection
								label="Folders"
								hasData={records.Folders?.length > 0}
								count={records.Folders?.length ?? 0}
								{...props}
							>
								<DevDataTable
									tableUniqueID="dev-obj-folders"
									data={omitFields(
										records.Folders,
										folderFields
									)}
									truncate={truncate}
								/>
							</DbSubSection>
							<DbSubSection
								label="DocumentVersions"
								hasData={records.DocumentVersions?.length > 0}
								count={records.DocumentVersions?.length ?? 0}
								{...props}
							>
								<DevDataTable
									tableUniqueID="dev-obj-document-versions"
									data={omitFields(
										records.DocumentVersions,
										refFields
									)}
									cellRenderers={mergeCellRenderers(
										makeDateCellRenderers(
											DB_DOCUMENT_VERSION_DATE_KEYS,
											friendlyValues
										),
										makeUserCellRenderers(
											DB_DOCUMENT_VERSION_USER_KEYS,
											userMap,
											friendlyValues
										),
										makeSizeCellRenderers(
											DB_DOCUMENT_VERSION_SIZE_KEYS,
											friendlyValues
										)
									)}
									truncate={truncate}
								/>
							</DbSubSection>
							<DbSubSection
								label="DocumentMajorVersions"
								hasData={
									records.DocumentMajorVersions?.length > 0
								}
								count={
									records.DocumentMajorVersions?.length ?? 0
								}
								{...props}
							>
								<DevDataTable
									tableUniqueID="dev-obj-document-major-versions"
									data={omitFields(
										records.DocumentMajorVersions,
										refFields
									)}
									cellRenderers={mergeCellRenderers(
										makeDateCellRenderers(
											DB_DOCUMENT_VERSION_DATE_KEYS,
											friendlyValues
										),
										makeUserCellRenderers(
											DB_DOCUMENT_VERSION_USER_KEYS,
											userMap,
											friendlyValues
										)
									)}
									truncate={truncate}
								/>
							</DbSubSection>
							<DbSubSection
								label="DocumentVersionCounters"
								hasData={!!records.DocumentVersionCounters}
								count={records.DocumentVersionCounters ? 1 : 0}
								{...props}
							>
								<DevTransposedTable
									tableUniqueID="dev-obj-document-version-counters"
									data={omitFields(
										records.DocumentVersionCounters,
										refFields
									)}
									truncate={truncate}
								/>
							</DbSubSection>
							<DbSubSection
								label="DocumentVersionLabels"
								hasData={
									records.DocumentVersionLabels?.length > 0
								}
								count={
									records.DocumentVersionLabels?.length ?? 0
								}
								{...props}
							>
								<DevDataTable
									tableUniqueID="dev-obj-document-version-labels"
									data={omitFields(
										records.DocumentVersionLabels,
										refFields
									)}
									truncate={truncate}
								/>
							</DbSubSection>
						</>
					) : objectType === "email" ? (
						<>
							<DbSubSection
								label="Emails"
								hasData={!!records.Emails}
								count={records.Emails ? 1 : 0}
								{...props}
							>
								<DevTransposedTable
									tableUniqueID="dev-obj-emails"
									data={omitFields(records.Emails, idFields)}
									cellRenderers={mergeCellRenderers(
										makeDateCellRenderers(
											DB_EMAIL_DATE_KEYS,
											friendlyValues
										),
										makeSizeCellRenderers(
											DB_EMAIL_SIZE_KEYS,
											friendlyValues
										)
									)}
									truncate={truncate}
								/>
							</DbSubSection>
							<DbSubSection
								label="Folders"
								hasData={records.Folders?.length > 0}
								count={records.Folders?.length ?? 0}
								{...props}
							>
								<DevDataTable
									tableUniqueID="dev-obj-email-folders"
									data={omitFields(
										records.Folders,
										folderFields
									)}
									truncate={truncate}
								/>
							</DbSubSection>
						</>
					) : (
						<>
							<DbSubSection
								label="Notes"
								hasData={!!records.Notes}
								count={records.Notes ? 1 : 0}
								{...props}
							>
								<DevTransposedTable
									tableUniqueID="dev-obj-notes"
									data={omitFields(records.Notes, idFields)}
									cellRenderers={mergeCellRenderers(
										makeDateCellRenderers(
											DB_NOTE_DATE_KEYS,
											friendlyValues
										),
										makeUserCellRenderers(
											DB_NOTE_USER_KEYS,
											userMap,
											friendlyValues
										)
									)}
									truncate={truncate}
								/>
							</DbSubSection>
							<DbSubSection
								label="Folders"
								hasData={records.Folders?.length > 0}
								count={records.Folders?.length ?? 0}
								{...props}
							>
								<DevDataTable
									tableUniqueID="dev-obj-note-folders"
									data={omitFields(
										records.Folders,
										folderFields
									)}
									truncate={truncate}
								/>
							</DbSubSection>
						</>
					)}
				</div>
			) : null}
		</div>
	);
}

function safeDecodeURIComponent(str) {
	try {
		return decodeURIComponent(str);
	} catch {
		return str;
	}
}

function parseDateValue(value) {
	if (value == null) return null;
	const n = Number(value);
	const d = isNaN(n) ? new Date(value) : new Date(n);
	return isNaN(d.getTime()) ? null : d;
}

const DATE_MODAL_TD_LABEL = {
	padding: "4px 8px",
	fontFamily: "monospace",
	fontSize: 12,
	fontWeight: 600,
	color: "#555",
	whiteSpace: "nowrap",
	border: "1px solid #eee",
	backgroundColor: "#f5f5f5",
};
const DATE_MODAL_TD_VALUE = {
	padding: "4px 8px",
	fontFamily: "monospace",
	fontSize: 12,
	border: "1px solid #eee",
	wordBreak: "break-all",
};
function FriendlyValuePopover({
	friendlyStr,
	rawStr,
	friendlyLabel,
	rawLabel,
}) {
	return (
		<table style={{ borderCollapse: "collapse" }}>
			<tbody>
				<tr>
					<td style={DATE_MODAL_TD_LABEL}>{friendlyLabel}</td>
					<td style={DATE_MODAL_TD_VALUE}>{friendlyStr}</td>
				</tr>
				<tr>
					<td style={DATE_MODAL_TD_LABEL}>{rawLabel}</td>
					<td style={DATE_MODAL_TD_VALUE}>{rawStr}</td>
				</tr>
			</tbody>
		</table>
	);
}

function DateCell({ value, friendlyValues }) {
	const parsed = parseDateValue(value);
	const isNull = value == null;
	const friendlyStr = parsed
		? parsed.toLocaleString()
		: isNull
			? "null"
			: String(value);
	const rawStr = isNull ? "null" : String(value);

	if (isNull) {
		return (
			<span
				style={{
					color: "#bbb",
					fontStyle: "italic",
					fontSize: 12,
					fontFamily: "monospace",
				}}
			>
				null
			</span>
		);
	}

	return (
		<HoverCard
			withinPortal
			openDelay={0}
			closeDelay={0}
			transitionProps={{ duration: 0 }}
			shadow="sm"
			position="top-start"
		>
			<HoverCard.Target>
				<span
					style={{
						fontSize: 12,
						fontFamily: "monospace",
						color: friendlyValues ? "#6b4f00" : "#222",
						cursor: "default",
					}}
				>
					{friendlyValues ? friendlyStr : rawStr}
				</span>
			</HoverCard.Target>
			<HoverCard.Dropdown style={{ padding: 4 }}>
				<FriendlyValuePopover
					friendlyStr={friendlyStr}
					rawStr={rawStr}
					friendlyLabel="Date"
					rawLabel="original"
				/>
			</HoverCard.Dropdown>
		</HoverCard>
	);
}

const S3_FLAT_DATE_KEYS = ["lastModified"];
const S3_METADATA_DATE_KEYS = [
	"documentaddeddate",
	"documentcreateddate",
	"modifieddate",
];
const SEARCH_DATE_KEYS = [
	"documentAddedDate",
	"documentCreatedDate",
	"emailAddedDate",
	"emailDate",
	"modifiedDate",
];
const DB_DOCUMENT_DATE_KEYS = ["createdAt"];
const DB_DOCUMENT_VERSION_DATE_KEYS = ["createdAt"];
const DB_EMAIL_DATE_KEYS = ["date"];
const DB_NOTE_DATE_KEYS = ["createdOn", "modifiedOn"];
const DB_DOCUMENT_USER_KEYS = ["createdBy", "lockedBy"];
const DB_DOCUMENT_VERSION_USER_KEYS = ["createdBy"];
const DB_NOTE_USER_KEYS = ["createdBy", "modifiedBy"];
const S3_METADATA_USER_KEYS = ["user"];
const SEARCH_USER_KEYS = [
	"documentAddedBy",
	"documentModifiedBy",
	"documentLockedBy",
	"matterNoteUpdatedBy",
];
const S3_FLAT_SIZE_KEYS = ["size"];
const DB_DOCUMENT_SIZE_KEYS = ["filesize"];
const DB_DOCUMENT_VERSION_SIZE_KEYS = ["filesize"];
const DB_EMAIL_SIZE_KEYS = ["filesize"];

function makeDateCellRenderers(keys, friendlyValues) {
	return Object.fromEntries(
		keys.map((k) => {
			const renderFn = (value) => (
				<DateCell value={value} friendlyValues={friendlyValues} />
			);
			renderFn.getStrings = (value) => {
				if (value == null) return null;
				const parsed = parseDateValue(value);
				return {
					friendlyStr: parsed
						? parsed.toLocaleString()
						: String(value),
					rawStr: String(value),
					friendlyLabel: "Date",
				};
			};
			return [k, renderFn];
		})
	);
}

function UserCell({ value, userMap, friendlyValues }) {
	const isNull = value == null;
	const displayName = !isNull ? (userMap[value] ?? null) : null;
	const friendlyStr = isNull ? "null" : (displayName ?? value);
	const rawStr = isNull ? "null" : String(value);

	if (isNull) {
		return (
			<span
				style={{
					color: "#bbb",
					fontStyle: "italic",
					fontSize: 12,
					fontFamily: "monospace",
				}}
			>
				null
			</span>
		);
	}

	return (
		<HoverCard
			withinPortal
			openDelay={0}
			closeDelay={0}
			transitionProps={{ duration: 0 }}
			shadow="sm"
			position="top-start"
		>
			<HoverCard.Target>
				<span
					style={{
						fontSize: 12,
						fontFamily: "monospace",
						color: friendlyValues ? "#6b4f00" : "#222",
						cursor: "default",
					}}
				>
					{friendlyValues ? friendlyStr : rawStr}
				</span>
			</HoverCard.Target>
			<HoverCard.Dropdown style={{ padding: 4 }}>
				<FriendlyValuePopover
					friendlyStr={friendlyStr}
					rawStr={rawStr}
					friendlyLabel="Display Name"
					rawLabel="original"
				/>
			</HoverCard.Dropdown>
		</HoverCard>
	);
}

function makeUserCellRenderers(keys, userMap, friendlyValues) {
	return Object.fromEntries(
		keys.map((k) => {
			const renderFn = (value) => (
				<UserCell
					value={value}
					userMap={userMap}
					friendlyValues={friendlyValues}
				/>
			);
			renderFn.getStrings = (value) => {
				if (value == null) return null;
				const displayName = userMap[value] ?? null;
				return {
					friendlyStr: displayName ?? String(value),
					rawStr: String(value),
					friendlyLabel: "Display Name",
				};
			};
			return [k, renderFn];
		})
	);
}

function SizeCell({ value, friendlyValues }) {
	const isNull = value == null;
	const friendlyStr = isNull ? "null" : humanFileSize(Number(value), true);
	const rawStr = isNull ? "null" : String(value);

	if (isNull) {
		return (
			<span
				style={{
					color: "#bbb",
					fontStyle: "italic",
					fontSize: 12,
					fontFamily: "monospace",
				}}
			>
				null
			</span>
		);
	}

	return (
		<HoverCard
			withinPortal
			openDelay={0}
			closeDelay={0}
			transitionProps={{ duration: 0 }}
			shadow="sm"
			position="top-start"
		>
			<HoverCard.Target>
				<span
					style={{
						fontSize: 12,
						fontFamily: "monospace",
						color: friendlyValues ? "#6b4f00" : "#222",
						cursor: "default",
					}}
				>
					{friendlyValues ? friendlyStr : rawStr}
				</span>
			</HoverCard.Target>
			<HoverCard.Dropdown style={{ padding: 4 }}>
				<FriendlyValuePopover
					friendlyStr={friendlyStr}
					rawStr={rawStr}
					friendlyLabel="Size"
					rawLabel="bytes"
				/>
			</HoverCard.Dropdown>
		</HoverCard>
	);
}

function makeSizeCellRenderers(keys, friendlyValues) {
	return Object.fromEntries(
		keys.map((k) => {
			const renderFn = (value) => (
				<SizeCell value={value} friendlyValues={friendlyValues} />
			);
			renderFn.getStrings = (value) => {
				if (value == null) return null;
				return {
					friendlyStr: humanFileSize(Number(value), true),
					rawStr: String(value),
					friendlyLabel: "Size",
				};
			};
			return [k, renderFn];
		})
	);
}

function mergeCellRenderers(...renderers) {
	const filtered = renderers.filter(Boolean);
	if (filtered.length === 0) return undefined;
	return Object.assign({}, ...filtered);
}

const S3_HIDDEN_FIELDS = ["proxyurl", "localstackapiid", "documentid"];
const SEARCH_HIDDEN_FIELDS = ["documentID", "ETag", "documentJoin"];
const DOCUMENT_SEARCH_RECORD_TYPE_ORDER = [
	"docs",
	"documentVersion",
	"documentMajorVersion",
];

function buildViewInS3Url(bucket, region, key) {
	if (!region) return null;
	return `https://${region}.console.aws.amazon.com/s3/object/${bucket}?region=${region}&prefix=${key
		.split("/")
		.map(encodeURIComponent)
		.join("/")}`;
}

function ObjectsList({
	objects,
	bucket,
	region,
	expectedKey,
	selectedKey,
	onSelectKey,
	friendlyValues,
}) {
	const [hoveredKey, setHoveredKey] = useState(null);

	if (!objects?.length) {
		return (
			<span style={{ color: "#aaa", fontSize: 12, fontStyle: "italic" }}>
				No records
			</span>
		);
	}

	const isSelectable = objects.length > 1;

	return (
		<div style={{ overflowX: "auto" }}>
			<table
				style={{
					borderCollapse: "collapse",
					fontSize: 12,
					fontFamily: "monospace",
					width: "100%",
					border: CELL_BORDER,
				}}
			>
				<thead>
					<tr>
						{["key", "size", "lastModified", "versions"].map(
							(h) => (
								<th
									key={h}
									style={{
										padding: "4px 8px",
										textAlign: "left",
										fontWeight: 700,
										whiteSpace: "nowrap",
										color: "#444",
										backgroundColor: "#f5f5f5",
										border: CELL_BORDER,
									}}
								>
									{h}
								</th>
							)
						)}
					</tr>
				</thead>
				<tbody>
					{objects.map((object, i) => {
						const isSelected = object.key === selectedKey;
						const isExpected = object.key === expectedKey;
						const viewInS3Url = buildViewInS3Url(
							bucket,
							region,
							object.key
						);
						return (
							<tr
								key={object.key}
								onClick={
									isSelectable
										? () => onSelectKey(object.key)
										: undefined
								}
								onMouseEnter={() => setHoveredKey(object.key)}
								onMouseLeave={() => setHoveredKey(null)}
								title={
									isSelectable
										? "Select to view versions"
										: undefined
								}
								style={{
									cursor: isSelectable
										? "pointer"
										: "default",
									borderLeft: isSelected
										? "3px solid #2f6fed"
										: "3px solid transparent",
									backgroundColor: isSelected
										? "#e7f0ff"
										: i % 2 === 0
											? "#fff"
											: "#fafafa",
								}}
							>
								<td
									style={{
										padding: "3px 8px",
										border: CELL_BORDER,
										wordBreak: "break-all",
										position: "relative",
										fontWeight: isSelected ? 700 : 400,
									}}
								>
									{object.key}
									{isExpected ? (
										<span
											style={{
												marginLeft: 6,
												color: "#888",
												fontWeight: 400,
												fontStyle: "italic",
											}}
										>
											(expected)
										</span>
									) : null}
									{hoveredKey === object.key ? (
										<span
											style={{
												position: "absolute",
												right: 2,
												top: "50%",
												transform: "translateY(-50%)",
												background: "white",
												display: "flex",
												alignItems: "center",
												gap: 2,
											}}
										>
											{viewInS3Url ? (
												<ActionIcon
													component="a"
													href={viewInS3Url}
													target="_blank"
													rel="noreferrer"
													title="View in S3"
													size="sm"
													variant="subtle"
													onClick={(e) =>
														e.stopPropagation()
													}
												>
													<IconBucket size={13} />
												</ActionIcon>
											) : null}
											<ButtonWithPopoverFeedback
												iconComponent={
													<IconCopy size={13} />
												}
												onClick={(e) => {
													e?.stopPropagation?.();
													navigator.clipboard.writeText(
														object.key
													);
												}}
												feedbackMessage="Copied key"
												title="Copy key"
											/>
										</span>
									) : null}
								</td>
								<td
									style={{
										padding: "3px 8px",
										border: CELL_BORDER,
										whiteSpace: "nowrap",
									}}
								>
									<SizeCell
										value={object.size}
										friendlyValues={friendlyValues}
									/>
								</td>
								<td
									style={{
										padding: "3px 8px",
										border: CELL_BORDER,
										whiteSpace: "nowrap",
									}}
								>
									<DateCell
										value={object.lastModified}
										friendlyValues={friendlyValues}
									/>
								</td>
								<td
									style={{
										padding: "3px 8px",
										border: CELL_BORDER,
										whiteSpace: "nowrap",
									}}
								>
									{object.versionCount}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

const S3_OBJECTS_ICON = (
	<IconBucket size={14} style={{ flexShrink: 0, color: "#888" }} />
);

function S3Section({
	s3,
	selectedKey,
	onSelectKey,
	openObjectsSections,
	onObjectsSectionsChange,
	openVersionsSections,
	onVersionsSectionsChange,
	versionsView,
	setVersionsView,
	showAllFields,
	friendlyValues,
	userMap,
	truncate,
}) {
	const [topRowHovered, setTopRowHovered] = useState(false);

	if (!s3) {
		return null;
	}

	const { bucket, region, prefix, expectedKey, objects, error } = s3;

	const selectedObject = selectedKey
		? (objects?.find((o) => o.key === selectedKey) ?? null)
		: null;
	const versions = selectedObject?.versions ?? [];
	const hasVersions = versions.length > 0;

	const topLabel = expectedKey ? "key" : "prefix";
	const topValue = expectedKey ?? prefix;
	const topViewInS3Url = expectedKey
		? buildViewInS3Url(bucket, region, expectedKey)
		: null;

	return (
		<div>
			<table style={{ borderCollapse: "collapse", width: "100%" }}>
				<tbody>
					<tr
						style={{ borderBottom: "1px solid #e8e8e8" }}
						onMouseEnter={() => setTopRowHovered(true)}
						onMouseLeave={() => setTopRowHovered(false)}
					>
						<td
							style={{
								padding: "4px 16px 4px 8px",
								color: "#555",
								fontWeight: 600,
								fontSize: 12,
								fontFamily: "monospace",
								whiteSpace: "nowrap",
								verticalAlign: "middle",
								width: "1px",
							}}
						>
							{topLabel}
						</td>
						<td
							style={{
								padding: "4px 4px 4px 8px",
								verticalAlign: "middle",
								position: "relative",
							}}
						>
							<span
								style={{
									display: "block",
									fontSize: 12,
									fontFamily: "monospace",
									wordBreak: "break-all",
								}}
							>
								{topValue}
							</span>
							{topRowHovered ? (
								<div
									style={{
										position: "absolute",
										right: 2,
										top: "50%",
										transform: "translateY(-50%)",
										background: "white",
										display: "flex",
										gap: 2,
									}}
								>
									{topViewInS3Url ? (
										<ActionIcon
											component="a"
											href={topViewInS3Url}
											target="_blank"
											rel="noreferrer"
											title="View in S3"
											size="sm"
											variant="subtle"
										>
											<IconBucket size={13} />
										</ActionIcon>
									) : null}
									<ButtonWithPopoverFeedback
										iconComponent={<IconCopy size={13} />}
										onClick={() =>
											navigator.clipboard.writeText(
												topValue
											)
										}
										feedbackMessage={
											expectedKey
												? "Copied key"
												: "Copied prefix"
										}
										title={
											expectedKey
												? "Copy key"
												: "Copy prefix"
										}
									/>
								</div>
							) : null}
						</td>
					</tr>
				</tbody>
			</table>
			{error ? (
				<div
					style={{
						marginTop: 12,
						padding: "8px 12px",
						background: "#fff3f3",
						border: "1px solid #f5c6c6",
						borderRadius: 4,
						color: "#b00",
						fontFamily: "monospace",
						fontSize: 12,
					}}
				>
					S3 error: {error}
				</div>
			) : null}
			<div style={{ marginTop: 12 }}>
				<DbSubSection
					label="Objects"
					icon={S3_OBJECTS_ICON}
					hasData={objects?.length > 0}
					count={objects?.length ?? 0}
					openSections={openObjectsSections}
					onSectionsChange={onObjectsSectionsChange}
				>
					<ObjectsList
						objects={objects}
						bucket={bucket}
						region={region}
						expectedKey={expectedKey}
						selectedKey={selectedKey}
						onSelectKey={onSelectKey}
						friendlyValues={friendlyValues}
					/>
				</DbSubSection>
			</div>
			<div style={{ marginTop: 12 }}>
				<DbSubSection
					label="Versions"
					hasData={!!selectedObject && hasVersions}
					count={versions.length}
					emptyLabel={
						objects?.length > 0 && !selectedObject
							? "Select an object above to view its versions"
							: "no records"
					}
					headerExtra={
						<SegmentedControl
							size="xs"
							value={versionsView}
							onChange={setVersionsView}
							data={[
								{ label: "S3", value: "flat" },
								{ label: "Metadata", value: "metadata" },
								{ label: "Tags", value: "tags" },
							]}
						/>
					}
					openSections={openVersionsSections}
					onSectionsChange={onVersionsSectionsChange}
				>
					{versionsView === "flat" ? (
						<DevDataTable
							tableUniqueID="dev-obj-s3-flat"
							cellRenderers={mergeCellRenderers(
								makeDateCellRenderers(
									S3_FLAT_DATE_KEYS,
									friendlyValues
								),
								makeSizeCellRenderers(
									S3_FLAT_SIZE_KEYS,
									friendlyValues
								)
							)}
							truncate={truncate}
							data={versions.map((v) => ({
								versionID: v.versionID,
								isLatest: v.isLatest,
								lastModified: v.lastModified,
								size: v.size,
								etag: v.etag,
								contentType: v.contentType,
								metadata: v.metadata
									? JSON.stringify(v.metadata)
									: null,
								tags:
									v.tags && Object.keys(v.tags).length > 0
										? JSON.stringify(v.tags)
										: null,
							}))}
						/>
					) : versionsView === "tags" ? (
						(() => {
							const allTagKeys = [
								...new Set(
									versions.flatMap((v) =>
										Object.keys(v.tags || {})
									)
								),
							].sort();
							if (allTagKeys.length === 0) {
								return (
									<span
										style={{
											color: "#aaa",
											fontSize: 12,
											fontStyle: "italic",
										}}
									>
										no s3 tags
									</span>
								);
							}
							return (
								<DevDataTable
									tableUniqueID="dev-obj-s3-tags"
									truncate={truncate}
									data={versions.map((v) => ({
										versionID: v.versionID,
										...Object.fromEntries(
											allTagKeys.map((k) => [
												k,
												v.tags?.[k] ?? null,
											])
										),
									}))}
								/>
							);
						})()
					) : (
						<DevDataTable
							tableUniqueID="dev-obj-s3-metadata"
							truncate={truncate}
							cellRenderers={mergeCellRenderers(
								makeDateCellRenderers(
									S3_METADATA_DATE_KEYS,
									friendlyValues
								),
								makeUserCellRenderers(
									S3_METADATA_USER_KEYS,
									userMap,
									friendlyValues
								)
							)}
							data={(() => {
								let allKeys = [
									...new Set(
										versions.flatMap((v) =>
											Object.keys(v.metadata || {})
										)
									),
								].sort();
								if (!showAllFields) {
									allKeys = allKeys.filter(
										(k) => !S3_HIDDEN_FIELDS.includes(k)
									);
								}
								return versions.map((v) => ({
									versionID: v.versionID,
									...Object.fromEntries(
										allKeys.map((k) => [
											k,
											v.metadata?.[k] != null
												? safeDecodeURIComponent(
														v.metadata[k]
													)
												: null,
										])
									),
								}));
							})()}
						/>
					)}
				</DbSubSection>
			</div>
		</div>
	);
}

function SearchSection({
	search,
	showAllFields,
	friendlyValues,
	userMap,
	openSections,
	onSectionsChange,
	truncate,
}) {
	if (!search) {
		return (
			<span style={{ color: "#aaa", fontSize: 12, fontStyle: "italic" }}>
				No search data
			</span>
		);
	}

	if (search.status === "ERROR") {
		return (
			<span style={{ color: "var(--lw-darker-red)", fontSize: 12 }}>
				Can&apos;t connect to search
				{search.error ? `: ${search.error}` : ""}
			</span>
		);
	}

	const { records } = search;

	const displayRecords = showAllFields
		? records
		: omitFields(records, SEARCH_HIDDEN_FIELDS);

	if (!displayRecords?.length) return null;

	const grouped = displayRecords.reduce((acc, record) => {
		const type = record.recordType ?? "unknown";
		if (!acc[type]) acc[type] = [];
		acc[type].push(record);
		return acc;
	}, {});

	const docTypes = DOCUMENT_SEARCH_RECORD_TYPE_ORDER.filter(
		(t) => grouped[t]
	);
	const otherTypes = Object.keys(grouped).filter(
		(t) => !DOCUMENT_SEARCH_RECORD_TYPE_ORDER.includes(t)
	);
	const orderedTypes = [...docTypes, ...otherTypes];

	const cellRenderers = mergeCellRenderers(
		makeDateCellRenderers(SEARCH_DATE_KEYS, friendlyValues),
		makeUserCellRenderers(SEARCH_USER_KEYS, userMap, friendlyValues)
	);

	if (orderedTypes.length === 1) {
		const type = orderedTypes[0];
		return (
			<DevDataTable
				tableUniqueID={`dev-obj-search-${type}`}
				data={grouped[type]}
				columnOrder={["recordType", "uploadSource", "VersionId"]}
				cellRenderers={cellRenderers}
				truncate={truncate}
			/>
		);
	}

	const subProps = { openSections, onSectionsChange };

	return (
		<div>
			{orderedTypes.map((type) => (
				<DbSubSection
					key={type}
					label={type}
					hasData={true}
					count={grouped[type].length}
					{...subProps}
				>
					<DevDataTable
						tableUniqueID={`dev-obj-search-${type}`}
						data={grouped[type]}
						columnOrder={[
							"recordType",
							"uploadSource",
							"VersionId",
						]}
						cellRenderers={cellRenderers}
						truncate={truncate}
					/>
				</DbSubSection>
			))}
		</div>
	);
}

const SETTINGS_KEY = "developerObjectInfo";
const DEFAULT_SETTINGS = {
	sections: [],
	dbSections: [],
	searchSections: [],
	s3ObjectsSections: [],
	s3VersionsSections: [],
	s3View: "flat",
	showAllFields: false,
	truncate: true,
	friendlyValues: true,
	orientation: "columns",
	columnsCollapsed: [],
};

function readSettings() {
	try {
		const raw = localStorage.getItem(SETTINGS_KEY);
		return raw
			? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) }
			: DEFAULT_SETTINGS;
	} catch {
		return DEFAULT_SETTINGS;
	}
}

const AdminDeveloperObjectInfo = () => {
	const { tenantID, documentID, emailID, matterNoteID } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [settings, setSettings] = useState(readSettings);
	const {
		sections: openSections,
		dbSections: openDbSections,
		searchSections: openSearchSections,
		s3ObjectsSections: openS3ObjectsSections,
		s3VersionsSections: openS3VersionsSections,
		s3View: versionsView,
		showAllFields,
		truncate,
		friendlyValues,
		orientation,
		columnsCollapsed,
	} = settings;

	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedS3Key, setSelectedS3Key] = useState(null);

	// Re-derive the selected object each time the S3 data is refreshed (new
	// object, tenant, or manual refresh) rather than persisting it, since a
	// stale key wouldn't necessarily still be present in the new object list.
	useEffect(() => {
		setSelectedS3Key(data?.s3?.defaultKey ?? null);
	}, [data?.s3]);

	const updateSettings = useCallback((patch) => {
		setSettings((prev) => {
			const next = { ...prev, ...patch };
			localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
			return next;
		});
	}, []);

	const setVersionsView = useCallback(
		(v) => updateSettings({ s3View: v }),
		[updateSettings]
	);
	const toggleShowAllFields = useCallback(
		() => updateSettings({ showAllFields: !showAllFields }),
		[updateSettings, showAllFields]
	);
	const toggleTruncate = useCallback(
		() => updateSettings({ truncate: !truncate }),
		[updateSettings, truncate]
	);
	const toggleFriendlyValues = useCallback(
		() => updateSettings({ friendlyValues: !friendlyValues }),
		[updateSettings, friendlyValues]
	);
	const setOrientation = useCallback(
		(v) => updateSettings({ orientation: v }),
		[updateSettings]
	);
	const toggleColumnCollapsed = useCallback(
		(section) => {
			const next = columnsCollapsed.includes(section)
				? columnsCollapsed.filter((s) => s !== section)
				: [...columnsCollapsed, section];
			updateSettings({ columnsCollapsed: next });
		},
		[updateSettings, columnsCollapsed]
	);

	const objectType =
		documentID != null ? "document" : emailID != null ? "email" : "note";

	const objectID = documentID ?? emailID ?? matterNoteID;

	const userMap = useMemo(() => data?.firm?.userMap ?? {}, [data]);

	// The ID is only ever "stored" in the URL - switching type or firm just
	// re-points the same numeric ID at the new URL and lets the load attempt
	// succeed or fail on its own.
	const handleObjectIDCommit = useCallback(
		(newObjectID) => {
			const pathSegment = OBJECT_TYPE_ROUTE_SEGMENT[objectType];
			navigate(`/admin/tenant/${tenantID}/${pathSegment}/${newObjectID}`);
		},
		[navigate, tenantID, objectType]
	);

	const handleObjectTypeChange = useCallback(
		(newObjectType) => {
			if (newObjectType === objectType) return;
			const pathSegment = OBJECT_TYPE_ROUTE_SEGMENT[newObjectType];
			navigate(`/admin/tenant/${tenantID}/${pathSegment}/${objectID}`);
		},
		[navigate, tenantID, objectType, objectID]
	);

	const handleTenantChange = useCallback(
		(newTenantID) => {
			if (newTenantID === tenantID) return;
			const pathSegment = OBJECT_TYPE_ROUTE_SEGMENT[objectType];
			navigate(`/admin/tenant/${newTenantID}/${pathSegment}/${objectID}`);
		},
		[navigate, tenantID, objectType, objectID]
	);

	useEffect(() => {
		dispatch(
			setSubheaderText({
				subheaderText: (
					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: 6,
						}}
					>
						{data?.tenants?.length ? (
							<TenantSelect
								tenants={data.tenants}
								value={tenantID}
								onChange={handleTenantChange}
							/>
						) : (
							<EditableIdSubheaderTitle
								value={tenantID}
								onCommit={handleTenantChange}
							/>
						)}
						<Select
							size="sm"
							data={OBJECT_TYPE_OPTIONS}
							value={objectType}
							onChange={handleObjectTypeChange}
							allowDeselect={false}
							styles={{
								input: {
									width: 130,
									height: 36,
									minHeight: 36,
								},
							}}
						/>
						<EditableIdSubheaderTitle
							value={objectID}
							onCommit={handleObjectIDCommit}
						/>
					</span>
				),
			})
		);
	}, [
		data?.tenants,
		tenantID,
		objectType,
		objectID,
		handleTenantChange,
		handleObjectIDCommit,
		handleObjectTypeChange,
		dispatch,
	]);

	const handleRefresh = useCallback(() => {
		setError(null);
		setLoading(true);
		fetchDeveloperObjectInfo({
			manualTenantID: Number(tenantID),
			documentID: documentID != null ? Number(documentID) : null,
			emailID: emailID != null ? Number(emailID) : null,
			matterNoteID: matterNoteID != null ? Number(matterNoteID) : null,
		})
			.then((result) => {
				setData(result);
				setLoading(false);
			})
			.catch((err) => {
				setError(err?.message ?? "Failed to load");
				setLoading(false);
			});
	}, [tenantID, documentID, emailID, matterNoteID]);

	useEffect(() => {
		handleRefresh();
	}, [tenantID, documentID, emailID, matterNoteID]);

	const handleSectionsChange = (newSections) =>
		updateSettings({ sections: newSections });

	const handleDbSectionsChange = (newSections) =>
		updateSettings({ dbSections: newSections });

	const handleSearchSectionsChange = (newSections) =>
		updateSettings({ searchSections: newSections });

	const handleS3ObjectsSectionsChange = (newSections) =>
		updateSettings({ s3ObjectsSections: newSections });

	const handleS3VersionsSectionsChange = (newSections) =>
		updateSettings({ s3VersionsSections: newSections });

	const allMainSections =
		objectType !== "note"
			? ["database", "s3", "search"]
			: ["database", "search"];

	const allDbSections =
		objectType === "document"
			? [
					"Documents",
					"Folders",
					"DocumentVersions",
					"DocumentMajorVersions",
					"DocumentVersionCounters",
					"DocumentVersionLabels",
				]
			: objectType === "email"
				? ["Emails"]
				: ["Notes"];

	const allSearchSections = [
		...new Set(
			(data?.search?.records ?? []).map((r) => r.recordType ?? "unknown")
		),
	];

	const allS3ObjectsSections = objectType !== "note" ? ["Objects"] : [];
	const allS3VersionsSections = objectType !== "note" ? ["Versions"] : [];

	const handleExpandAll = useCallback(() => {
		updateSettings({
			sections: allMainSections,
			dbSections: allDbSections,
			searchSections: allSearchSections,
			s3ObjectsSections: allS3ObjectsSections,
			s3VersionsSections: allS3VersionsSections,
			...(orientation === "columns" ? { columnsCollapsed: [] } : {}),
		});
	}, [
		updateSettings,
		allMainSections,
		allDbSections,
		allSearchSections,
		allS3ObjectsSections,
		allS3VersionsSections,
		orientation,
	]);

	const handleCollapseAll = useCallback(() => {
		updateSettings({
			sections: [],
			dbSections: [],
			searchSections: [],
			s3ObjectsSections: [],
			s3VersionsSections: [],
		});
	}, [updateSettings]);

	const isAnyExpanded =
		openSections.length > 0 ||
		openDbSections.length > 0 ||
		openSearchSections.length > 0 ||
		openS3ObjectsSections.length > 0 ||
		openS3VersionsSections.length > 0;

	useEffect(() => {
		dispatch(
			setSubheaderActions(
				<>
					<ActionIcon
						variant="default"
						size={30}
						onClick={handleRefresh}
						title="Refresh"
					>
						<IconRefresh size={14} />
					</ActionIcon>
					<ActionIcon
						variant={truncate ? "filled" : "default"}
						size={30}
						onClick={toggleTruncate}
						title={
							truncate ? "Show full content" : "Truncate content"
						}
					>
						<IconAlignJustified size={14} />
					</ActionIcon>
					<ActionIcon
						variant="default"
						size={30}
						onClick={
							isAnyExpanded ? handleCollapseAll : handleExpandAll
						}
						title={isAnyExpanded ? "Collapse all" : "Expand all"}
					>
						{isAnyExpanded ? (
							<IconChevronsUp size={14} />
						) : (
							<IconChevronsDown size={14} />
						)}
					</ActionIcon>
					<Menu position="bottom-end" withinPortal>
						<Menu.Target>
							<Button
								size="xs"
								variant="default"
								leftSection={<IconSettings size={14} />}
								rightSection={<IconChevronDown size={12} />}
							>
								Options
							</Button>
						</Menu.Target>
						<Menu.Dropdown>
							{data ? (
								<>
									<Menu.Divider />
									<Menu.Item
										onClick={() =>
											navigator.clipboard.writeText(
												JSON.stringify(data, null, 2)
											)
										}
									>
										Copy all JSON
									</Menu.Item>
								</>
							) : null}
							<Menu.Divider />
							<Menu.Label>Orientation</Menu.Label>
							<Menu.Item
								closeMenuOnClick={false}
								onClick={() => setOrientation("rows")}
								leftSection={
									orientation === "rows" ? (
										<IconCheck size={14} />
									) : (
										<span style={{ width: 14 }} />
									)
								}
							>
								Rows
							</Menu.Item>
							<Menu.Item
								closeMenuOnClick={false}
								onClick={() => setOrientation("columns")}
								leftSection={
									orientation === "columns" ? (
										<IconCheck size={14} />
									) : (
										<span style={{ width: 14 }} />
									)
								}
							>
								Columns
							</Menu.Item>
							<Menu.Divider />
							<Menu.Item
								closeMenuOnClick={false}
								onClick={toggleShowAllFields}
							>
								<span
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
									}}
								>
									<Checkbox
										size="xs"
										checked={showAllFields}
										readOnly
										tabIndex={-1}
									/>
									Show all fields
								</span>
							</Menu.Item>
							<Menu.Item
								closeMenuOnClick={false}
								onClick={toggleFriendlyValues}
							>
								<span
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
									}}
								>
									<Checkbox
										size="xs"
										checked={friendlyValues}
										readOnly
										tabIndex={-1}
									/>
									Show friendly values
								</span>
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				</>
			)
		);
		return () => dispatch(setSubheaderActions(null));
	}, [
		dispatch,
		data,
		showAllFields,
		friendlyValues,
		truncate,
		orientation,
		objectType,
		isAnyExpanded,
		handleExpandAll,
		handleCollapseAll,
		handleRefresh,
		toggleShowAllFields,
		toggleFriendlyValues,
		toggleTruncate,
		setOrientation,
	]);

	if (error && !data) {
		return (
			<div style={{ padding: "8px 16px", color: "var(--lw-darker-red)" }}>
				{error}
			</div>
		);
	}

	if (!data) {
		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "48px 16px",
				}}
			>
				<Loader size="xl" />
			</div>
		);
	}

	if (data.error) {
		return (
			<div style={{ padding: "8px 16px", color: "var(--lw-darker-red)" }}>
				{data.error}
			</div>
		);
	}

	const loadingOverlay = loading ? (
		<div
			style={{
				position: "absolute",
				inset: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: "rgba(255, 255, 255, 0.55)",
				zIndex: 10,
			}}
		>
			<Loader size="xl" />
		</div>
	) : null;

	const dbSection = (
		<DatabaseSection
			database={data.database}
			objectType={objectType}
			openDbSections={openDbSections}
			onDbSectionsChange={handleDbSectionsChange}
			showAllFields={showAllFields}
			friendlyValues={friendlyValues}
			userMap={userMap}
			truncate={truncate}
		/>
	);
	const s3Section =
		objectType !== "note" ? (
			<S3Section
				s3={data.s3}
				selectedKey={selectedS3Key}
				onSelectKey={setSelectedS3Key}
				openObjectsSections={openS3ObjectsSections}
				onObjectsSectionsChange={handleS3ObjectsSectionsChange}
				openVersionsSections={openS3VersionsSections}
				onVersionsSectionsChange={handleS3VersionsSectionsChange}
				versionsView={versionsView}
				setVersionsView={setVersionsView}
				showAllFields={showAllFields}
				friendlyValues={friendlyValues}
				userMap={userMap}
				truncate={truncate}
			/>
		) : null;
	const searchSection = (
		<SearchSection
			search={data.search}
			showAllFields={showAllFields}
			friendlyValues={friendlyValues}
			userMap={userMap}
			openSections={openSearchSections}
			onSectionsChange={handleSearchSectionsChange}
			truncate={truncate}
		/>
	);

	if (orientation === "columns") {
		const columns = [
			{
				key: "database",
				title: "Database",
				sectionControl: (
					<SectionControl
						label="Database"
						count={countDbRecords(data.database, objectType)}
					/>
				),
				content: dbSection,
			},
			...(objectType !== "note"
				? [
						{
							key: "s3",
							title: "S3",
							sectionControl: (
								<SectionControl
									label="S3"
									count={data.s3?.objects?.length ?? 0}
								/>
							),
							content: s3Section,
						},
					]
				: []),
			{
				key: "search",
				title: "Search",
				sectionControl: (
					<SectionControl
						label="Search"
						count={data.search?.records?.length ?? 0}
					/>
				),
				content: searchSection,
			},
		];

		return (
			<div
				className="noDefaultMargin"
				style={{
					display: "flex",
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					position: "relative",
				}}
			>
				{loadingOverlay}
				{columns.map((col, i) => (
					<ColumnPane
						key={col.key}
						title={col.title}
						sectionControl={col.sectionControl}
						collapsed={columnsCollapsed.includes(col.key)}
						onToggle={() => toggleColumnCollapsed(col.key)}
						isLast={i === columns.length - 1}
					>
						{col.content}
					</ColumnPane>
				))}
			</div>
		);
	}

	return (
		<div style={{ padding: "8px 16px", position: "relative" }}>
			{loadingOverlay}
			<Accordion
				multiple
				value={openSections}
				onChange={handleSectionsChange}
				transitionDuration={0}
				vars={() => ({
					root: { "--accordion-control-height": "26px" },
				})}
				styles={{
					item: { borderBottom: "1px solid #e0e0e0" },
					control: {
						fontWeight: 600,
						minHeight: 26,
						padding: "1px 0",
					},
					panel: { padding: 0 },
					content: { padding: "2px 0 4px 0" },
				}}
			>
				<Accordion.Item value="database">
					<Accordion.Control>
						<SectionControl
							label="Database"
							count={countDbRecords(data.database, objectType)}
						/>
					</Accordion.Control>
					<Accordion.Panel>{dbSection}</Accordion.Panel>
				</Accordion.Item>

				{objectType !== "note" ? (
					<Accordion.Item value="s3">
						<Accordion.Control>
							<SectionControl
								label="S3"
								count={data.s3?.objects?.length ?? 0}
							/>
						</Accordion.Control>
						<Accordion.Panel>{s3Section}</Accordion.Panel>
					</Accordion.Item>
				) : null}

				<Accordion.Item value="search">
					<Accordion.Control>
						<SectionControl
							label="Search"
							count={data.search?.records?.length ?? 0}
						/>
					</Accordion.Control>
					<Accordion.Panel>{searchSection}</Accordion.Panel>
				</Accordion.Item>
			</Accordion>
		</div>
	);
};

export default AdminDeveloperObjectInfo;
